import { Box, darken, lighten, rem } from '@mantine/core'

import { getBoggleBadgeMedallionColors } from '~/boggle/config/badges/categories'
import { BOGGLE_TIER_ROMAN } from '~/boggle/config/badges/familyTypes'
import type {
  BoggleBadgeDefinition,
  BoggleBadgeLevel,
} from '~/boggle/config/badges/types'
import type { BoggleRunSummaryRatingId } from '~/boggle/config/runSummaryScore'
import { getBoggleBadgeIcon } from '~/boggle/lib/badges/iconLookup'
import { IconGlyph } from '~/boggle/ui/atoms/Icons'
import { RatingBadge } from '~/boggle/ui/ratings/RatingBadge'
import {
  PENTAGON_ROTATION_RADIANS,
  regularPolygonClipPath,
} from '~/boggle/ui/run/pixi/tileShapeDraw'

import classes from './BadgeMedallion.module.css'

/**
 * Rarity → silhouette mapping in the medallion grammar:
 * - Common = circle
 * - Uncommon = square
 * - Rare = diamond
 * - Legendary = hexagon
 * - Impossible = pentagon
 */
const IMPOSSIBLE_PENTAGON_CLIP_PATH = regularPolygonClipPath(5, PENTAGON_ROTATION_RADIANS)

const RARITY_SHAPE_CLASS: Record<BoggleBadgeLevel, string> = {
  common: classes.shapeCircle,
  impossible: '',
  legendary: classes.shapeHexagon,
  rare: classes.shapeDiamond,
  uncommon: classes.shapeSquare,
}

export type BadgeMedallionSize = 'compact' | 'lg' | 'md' | 'sm'

/**
 * Authored medallion sizes. `sm` and `lg` match the post-run reveal contract
 * (46px settled list slot, ~86px bottom-of-screen reveal medallion).
 * `compact` is the Compendium grid footprint (~10px smaller than `md`).
 */
const SIZE_PX: Record<BadgeMedallionSize, number> = {
  compact: 52,
  lg: 86,
  md: 62,
  sm: 46,
}

/** Legendary hex / impossible pent outer width at `md`. `sm` / `lg` scale proportionally. */
const POLYGON_WIDTH_MD_PX = 55

/**
 * Per-rarity scale applied to the base size. Uncommon (square) and Rare
 * (diamond) badges read as visually smaller than Common (circle) so rarity is
 * communicated by shape *and* footprint without needing extra labels.
 * Legendary stays at 1.0 — the hexagon already feels distinct.
 */
const RARITY_SCALE: Record<BoggleBadgeLevel, number> = {
  common: 0.9,
  impossible: 1,
  uncommon: 0.85,
  rare: 1,
  legendary: 1,
}

/** Icon size as a fraction of the medallion size. */
const ICON_FRACTION = 0.5

/** Tier bubble metrics at `compact` (Compendium grid size). Other sizes scale proportionally. */
const TIER_BUBBLE_REF_SIZE: BadgeMedallionSize = 'compact'
const TIER_BUBBLE_AT_REF = {
  borderRadius: 6,
  bottom: -2,
  fontSize: 10,
  letterSpacing: 1,
  paddingLeft: 1,
  width: 21,
} as const

/**
 * Temporary, authored medallion until bespoke art exists. Category owns color
 * (icon + frame; background = darker hue), rarity owns silhouette. Locked
 * badges keep their real icon + shape but render grayscale + brightness 0.25.
 *
 * When `locked` is used for Compendium filter mismatch, `highestEarnedRating`
 * still shows the player's true best rating in the corner.
 */
export function BadgeMedallion({
  definition,
  highestEarnedRating = null,
  locked,
  size = 'md',
}: {
  definition: BoggleBadgeDefinition
  /** Highest rating earned for this badge; renders in lower-left when present. */
  highestEarnedRating?: BoggleRunSummaryRatingId | null
  /** Locked treatment applies grayscale + brightness 0.25 to medallion and tier bubble. */
  locked: boolean
  size?: BadgeMedallionSize
}) {
  const { background, frame, frameBorder } = getBoggleBadgeMedallionColors({
    badgeId: definition.id,
    category: definition.category,
  })
  const Icon = getBoggleBadgeIcon(definition.iconId)
  const px = Math.round(SIZE_PX[size] * RARITY_SCALE[definition.level])
  const widthPx =
    definition.level === 'legendary'
      ? Math.round((POLYGON_WIDTH_MD_PX / SIZE_PX.md) * SIZE_PX[size])
      : px
  const iconPx = Math.round(px * ICON_FRACTION)
  const shapeClass = RARITY_SHAPE_CLASS[definition.level]
  const showRatingCorner =
    highestEarnedRating !== null && highestEarnedRating !== 'none'
  const showTierBubble =
    definition.tierIndex !== undefined && definition.tierIndex > 1
  const tierRoman = showTierBubble
    ? (BOGGLE_TIER_ROMAN[definition.tierIndex! - 1] ?? String(definition.tierIndex))
    : null

  const isHexagon = definition.level === 'legendary'
  const isPentagon = definition.level === 'impossible'
  const isDiamond = definition.level === 'rare'

  const diamondFillStyle = isDiamond
    ? {
        background: `linear-gradient(128deg, ${lighten(background, 0.1)} 0%, ${background} 58%, ${darken(background, 0.07)} 100%)`,
      }
    : undefined

  const legendaryFillStyle = isHexagon
    ? {
        background: `radial-gradient(ellipse 95% 88% at 22% 18%, ${lighten(background, 0.12)} 0%, ${background} 52%, ${darken(background, 0.06)} 100%)`,
      }
    : undefined

  const pentagonFillStyle = isPentagon
    ? {
        background: `radial-gradient(ellipse 95% 88% at 22% 18%, ${lighten(background, 0.12)} 0%, ${background} 52%, ${darken(background, 0.06)} 100%)`,
      }
    : undefined

  const shapeBody = isHexagon ? (
    <Box
      className={`${classes.hexFrame} ${shapeClass}`}
      data-shape={definition.level}
      h={px}
      style={{ background: frameBorder }}
      w={widthPx}
    >
      <Box
        className={`${classes.medallion} ${classes.hexInner} ${classes.hexInnerLegendary} ${shapeClass}`}
        style={legendaryFillStyle}
      >
        {!locked ? <span aria-hidden className={classes.legendarySheen} /> : null}
        <Box className={classes.legendaryIcon}>
          <IconGlyph color={frame} icon={Icon} size={iconPx} weight="duotone" />
        </Box>
      </Box>
    </Box>
  ) : isPentagon ? (
    <Box
      className={classes.pentFrame}
      data-shape={definition.level}
      h={px}
      style={{ background: frameBorder, clipPath: IMPOSSIBLE_PENTAGON_CLIP_PATH }}
      w={px}
    >
      <Box
        className={`${classes.medallion} ${classes.pentInner}`}
        style={{ ...pentagonFillStyle, clipPath: IMPOSSIBLE_PENTAGON_CLIP_PATH }}
      >
        {!locked ? <span aria-hidden className={classes.legendarySheen} /> : null}
        <Box className={classes.legendaryIcon}>
          <IconGlyph color={frame} icon={Icon} size={iconPx} weight="duotone" />
        </Box>
      </Box>
    </Box>
  ) : isDiamond ? (
    <Box
      className={`${classes.diamondFrame} ${shapeClass}`}
      data-shape={definition.level}
      h={px}
      style={{ background: frameBorder }}
      w={px}
    >
      <Box
        className={`${classes.medallion} ${classes.diamondInner} ${shapeClass}`}
        style={diamondFillStyle}
      >
        <IconGlyph color={frame} icon={Icon} size={iconPx} weight="duotone" />
      </Box>
    </Box>
  ) : (
    <Box
      bd={`2px solid ${frameBorder}`}
      bg={background}
      className={`${classes.medallion} ${shapeClass}`}
      data-shape={definition.level}
      h={px}
      w={px}
    >
      <IconGlyph color={frame} icon={Icon} size={iconPx} weight="duotone" />
    </Box>
  )

  const footprintW = isHexagon ? Math.max(px, widthPx) : px

  /** Fixed slot for tier bubble — not scaled by `RARITY_SCALE` so the indicator aligns across levels. */
  const tierSlotPx = SIZE_PX[size]
  const tierSlotWidthPx = Math.round((POLYGON_WIDTH_MD_PX / SIZE_PX.md) * SIZE_PX[size])
  const layoutH = tierRoman ? rem(tierSlotPx) : px
  const layoutW = tierRoman ? rem(Math.max(tierSlotPx, tierSlotWidthPx)) : footprintW

  const tierBubbleScale = SIZE_PX[size] / SIZE_PX[TIER_BUBBLE_REF_SIZE]
  const tierBubbleStyle = tierRoman
    ? {
        background,
        border: `${rem(1)} solid ${frameBorder}`,
        borderRadius: rem(TIER_BUBBLE_AT_REF.borderRadius * tierBubbleScale),
        bottom: rem(TIER_BUBBLE_AT_REF.bottom * tierBubbleScale),
        color: frame,
        fontSize: rem(TIER_BUBBLE_AT_REF.fontSize * tierBubbleScale),
        letterSpacing: rem(TIER_BUBBLE_AT_REF.letterSpacing * tierBubbleScale),
        padding: `0 0 0 ${rem(TIER_BUBBLE_AT_REF.paddingLeft * tierBubbleScale)}`,
        width: rem(TIER_BUBBLE_AT_REF.width * tierBubbleScale),
      }
    : undefined

  return (
    <Box
      pos="relative"
      h={layoutH}
      w={layoutW}
      style={{
        alignItems: 'center',
        display: 'flex',
        flexShrink: 0,
        justifyContent: 'center',
      }}
    >
      <Box
        className={locked ? classes.locked : undefined}
        data-testid={`badge-medallion-${definition.id}`}
        data-locked={locked ? 'true' : 'false'}
        style={{
          display: 'inline-flex',
          flexShrink: 0,
        }}
      >
        {shapeBody}
      </Box>
      {tierRoman ? (
        <Box
          aria-hidden
          className={`${classes.tierBubble} ${locked ? classes.locked : ''}`}
          data-testid={`badge-medallion-tier-${definition.id}`}
          style={tierBubbleStyle}
        >
          {tierRoman}
        </Box>
      ) : null}
      {showRatingCorner ? (
        <Box aria-hidden className={classes.ratingCorner}>
          <RatingBadge
            className={classes.ratingCornerImage}
            ratingId={highestEarnedRating}
            testId={`badge-medallion-rating-${definition.id}`}
          />
        </Box>
      ) : null}
    </Box>
  )
}
